/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {
  ListKeyboardDelegate,
  useFocusRing,
  useKeyboard,
  useListBox,
  useOption,
  type AriaListBoxOptions,
  type Key,
} from "react-aria";

import { Item, useListState, type ListState } from "react-stately";
import { mergeProps } from "@react-aria/utils";

import { cssFns } from "@superweb/css";
import { useUiColors } from "../theme";
import { useTypo } from "../typo";

const SIZES = {
  s: { height: "32px" },
  m: { height: "56px" },
};

export type CalendarGridItem = {
  key: string;
  label: string;
  ariaLabel?: string;
  selected?: boolean;
  disabled?: boolean;
  highlighted?: boolean;
  disabledDescription?: string;
  description?: string;
  render?: () => ReactNode;
};

/**
 * Component for display a calendar months or years in popover.
 * @internal
 * Used listbox with layout grid options
 */
const ListBox = <T extends {}>({
  ariaListBoxProps,
  listBoxRef,
  state,
  options,
  onItemFocus,
  onArrowNavigation,
  direction,
  columns,
  size,
  onEscape,
  autoFocus,
  priorityKey,
}: {
  columns: number;
  ariaListBoxProps: AriaListBoxOptions<T>;
  listBoxRef: RefObject<HTMLDivElement>;
  state: ListState<T>;
  options: CalendarGridItem[];
  onItemFocus: (key: Key) => void;
  onArrowNavigation: (
    keyCode: "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight",
  ) => void;
  direction: "ltr" | "rtl";
  size: "s" | "m";
  onEscape?: () => void;
  autoFocus?: boolean;
  priorityKey?: string | null;
}) => {
  const optionsMap: Record<string, CalendarGridItem> = {};
  options.forEach((option) => {
    optionsMap[option.key] = option;
  });

  const [focusedKey, setFocusedKey] = useState<null | string | number>(
    state.selectionManager.focusedKey,
  );

  const keyboardDelegate = new ListKeyboardDelegate({
    collection: state.collection,
    ref: listBoxRef,
    disabledKeys: state.disabledKeys,
    layout: "grid",
    orientation: "vertical",
    direction,
  });

  const { focusProps, isFocused } = useFocusRing({
    within: true,
  });

  const { listBoxProps } = useListBox(
    {
      ...ariaListBoxProps,
      keyboardDelegate,
    },
    state,
    listBoxRef,
  );

  const { keyboardProps } = useKeyboard({
    onKeyDown: (e: ReactKeyboardEvent) => {
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
        onArrowNavigation(
          e.key as "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight",
        );
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onEscape?.();
      }
    },
  });

  useEffect(() => {
    const currentFocusedKey = state.selectionManager.focusedKey;
    if (currentFocusedKey && currentFocusedKey !== focusedKey) {
      onItemFocus(currentFocusedKey);
    }
    setFocusedKey(currentFocusedKey);
  }, [state.selectionManager.focusedKey, focusedKey, onItemFocus]);

  return (
    <div
      {...mergeProps(listBoxProps, focusProps, keyboardProps)}
      ref={listBoxRef}
      css={{
        ...cssFns.padding("0"),
        ...cssFns.border({ width: "0" }),
        ...cssFns.margin("0"),
        ...cssFns.gap("2px"),
        position: "relative",
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridAutoFlow: "row",
      }}
    >
      {Array.from(state.collection.getKeys()).map((key: Key) => {
        const item = state.collection.getItem(key);
        if (!item) {
          return null;
        }

        const option = optionsMap[item.key as string];
        if (!option) {
          return null;
        }

        return (
          <Option
            key={item.key}
            item={{
              key: item.key as string,
              rendered: option.render ? option.render() : option.label,
              ariaLabel: option.ariaLabel,
              selected: option.selected,
              disabled: option.disabled,
              highlighted: option.highlighted,
            }}
            listboxFocus={isFocused}
            state={state}
            autoFocus={autoFocus}
            priorityKey={priorityKey}
            size={size}
          />
        );
      })}
    </div>
  );
};

const Option = <T extends {}>({
  item,
  state,
  listboxFocus,
  autoFocus,
  priorityKey,
  size,
}: {
  item: {
    key: string;
    rendered: ReactNode;
    ariaLabel?: string;
    selected?: boolean;
    disabled?: boolean;
    highlighted?: boolean;
  };
  state: ListState<T>;
  listboxFocus: boolean;
  autoFocus?: boolean;
  priorityKey?: string | null;
  size: "s" | "m";
}) => {
  const ref = useRef<HTMLButtonElement>(null);
  const uiColors = useUiColors();
  const typo = useTypo();
  const [hasAutoFocused, setHasAutoFocused] = useState(false);

  const isFocused =
    listboxFocus && state.selectionManager.focusedKey === item.key;
  const { optionProps } = useOption({ key: item.key }, state, ref);

  useEffect(() => {
    if (
      autoFocus &&
      priorityKey === item.key &&
      !hasAutoFocused &&
      ref.current &&
      document.activeElement !== ref.current
    ) {
      ref.current.focus({ preventScroll: true });
      setHasAutoFocused(true);
    }
  }, [autoFocus, priorityKey, item.key, hasAutoFocused]);

  return (
    <button
      ref={ref}
      css={{
        ...SIZES[size],
        opacity: item.disabled ? "0.5" : "1",
        cursor: item.disabled ? "default" : "pointer",
        boxSizing: "border-box",
        position: "relative",
        flexDirection: "column",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        outlineStyle: "none",
        backgroundColor: item.selected
          ? uiColors.controlMain
          : item.highlighted
            ? uiColors.backgroundMinor
            : "transparent",
        color: item.selected ? uiColors.textOnControlMain : uiColors.text,
        ...cssFns.border({ style: "none", radius: "8px" }),
        ...cssFns.padding("0", "8px"),
        ...cssFns.boxShadow(
          isFocused && {
            inset: true,
            spreadRadius: "2px",
            color: uiColors.focus,
          },
        ),
        ...(size === "s"
          ? {
              ...cssFns.border({ radius: "8px" }),
              ...typo({
                level: "caption1",
                density: "tight",
                weight: "regular",
              }),
            }
          : {
              ...cssFns.border({ radius: "14px" }),
              ...typo({
                level: "body2",
                density: "normal",
                weight: "regular",
              }),
            }),
      }}
      {...mergeProps(optionProps)}
      aria-label={item.ariaLabel}
      aria-selected={item.selected ?? false}
    >
      {item.rendered}
    </button>
  );
};

export const OptionContent = ({
  label,
  disabled = false,
  selected,
}: {
  label: string;
  disabled?: boolean;
  selected?: boolean;
  size?: "s" | "m";
}) => {
  const uiColors = useUiColors();

  return (
    <div
      css={{
        display: "grid",
        alignItems: "center",
        color: selected
          ? uiColors.textOnControlMain
          : disabled
            ? uiColors.textMinor
            : uiColors.text,
      }}
    >
      <div
        css={{
          display: "grid",
        }}
      >
        <span
          css={{
            ...cssFns.overflow("hidden"),
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
      </div>
    </div>
  );
};

export const CalendarGrid = ({
  selectionMode = "single",
  options,
  columns,
  size = "m",
  initialFocusedKey,
  shouldFocusOnHover = false,
  onChange = () => {},
  onArrowNavigation = () => {},
  onItemFocus = () => {},
  label,
  ariaLabel,
  onEscape = () => {},
  autoFocus,
}: {
  selectionMode?: "single" | "multiple";
  options: CalendarGridItem[];
  columns: number;
  label: string;
  ariaLabel?: string;
  size?: "s" | "m";
  initialFocusedKey?: string;
  shouldFocusOnHover?: boolean;
  onChange?: (keys: string[]) => void;
  onArrowNavigation?: (
    keyСode: "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight",
    state: ListState<CalendarGridItem>,
  ) => void;
  onItemFocus?: (key: Key, state: ListState<CalendarGridItem>) => void;
  onEscape?: () => void;
  autoFocus?: boolean;
}) => {
  const listBoxRef = useRef<HTMLDivElement>(null);

  const handleChange = (keys: string[]) => {
    onChange(keys);
  };

  const handleArrowNavigation = (
    keyСode: "ArrowUp" | "ArrowDown" | "ArrowLeft" | "ArrowRight",
    state: ListState<CalendarGridItem>,
  ) => {
    onArrowNavigation(keyСode, state);
  };

  const handleItemFocus = (key: Key, state: ListState<CalendarGridItem>) => {
    onItemFocus(key, state);
  };

  const disabledKeys: string[] = [];
  options.forEach((option) => {
    if (option.disabled) {
      disabledKeys.push(option.key);
    }
  });

  const listState = useListState<CalendarGridItem>({
    selectionMode,
    disabledKeys,
    disallowEmptySelection: true,
    items: options.map((option) => ({ ...option, key: option.key })),
    children: (option: CalendarGridItem) => (
      <Item key={option.key} textValue={option.label}>
        <OptionContent
          label={option.label}
          disabled={option.disabled}
          selected={option.selected}
        />
      </Item>
    ),
    onSelectionChange: (options) => {
      const keys = [...options];
      handleChange(keys.map((key) => String(key)));
    },
  });

  const priorityKey = (() => {
    if (!autoFocus) return null;

    const selectedOption = options.find((option) => option.selected);
    if (selectedOption) {
      return selectedOption.key;
    }

    return options[0]?.key || null;
  })();

  const [isInitialMount, setInitialMount] = useState(true);

  useEffect(() => {
    if (initialFocusedKey && isInitialMount) {
      listState.selectionManager.setFocusedKey(initialFocusedKey);
      setInitialMount(false);
    }
  }, [initialFocusedKey, listState.selectionManager, isInitialMount]);

  return (
    <ListBox
      ariaListBoxProps={{
        label: ariaLabel || label,
        shouldFocusOnHover,
        shouldFocusWrap: false,
        disallowEmptySelection: false,
        shouldSelectOnPressUp: true,
      }}
      size={size}
      columns={columns}
      direction="ltr"
      listBoxRef={listBoxRef}
      state={listState}
      options={options}
      onArrowNavigation={(key) => {
        handleArrowNavigation(key, listState);
      }}
      onItemFocus={(key) => handleItemFocus(key, listState)}
      onEscape={onEscape}
      autoFocus={autoFocus}
      priorityKey={priorityKey}
    />
  );
};
